<template>
  <dashboard-layout title="Email Threads">
    <template #actions>
      <search-box v-model="searchQuery" placeholder="Search subject, from…" />
    </template>

    <stat-grid>
      <stat-card label="Total threads" :value="threads.length.toLocaleString('en-US')" icon="inbox" accent />
      <stat-card label="Pending" :value="counts.pending.toLocaleString('en-US')" icon="bell" />
      <stat-card label="Replied" :value="counts.replied.toLocaleString('en-US')" icon="clipboardcheck" />
    </stat-grid>

    <data-panel flush>
      <template #title>Threads <span class="muted">({{ filteredThreads.length.toLocaleString('en-US') }})</span></template>
      <template #actions>
        <filter-chips v-model="activeFilter" :options="filterOptions" />
      </template>

      <loading-state v-if="loading" label="Loading threads…" />
      <data-table
        v-else
        :columns="columns"
        :rows="filteredThreads"
        row-key="id"
        clickable
        @row-click="openThread"
      >
        <template #cell-status="{ row }">
          <status-badge :variant="statusVariant(row)" :label="formatThreadStatus(row)" />
        </template>
        <template #cell-customer="{ row }">
          <div v-if="row.customerName" class="cust-name">{{ row.customerName }}</div>
          <div class="cust-email">{{ row.customerEmail || '—' }}</div>
        </template>
        <template #cell-subject="{ row }">
          <span class="subject" :title="row.subject">{{ truncate(row.subject, 80) }}</span>
        </template>
        <template #cell-messages="{ row }">
          <span class="msg-cell">
            <status-badge variant="primary" :label="`${row.messageCount || 0} msg`" />
            <status-badge v-if="row.pendingCount > 0" variant="warning" :label="`${row.pendingCount} pending`" />
          </span>
        </template>
        <template #cell-lastActivityAt="{ row }">
          <span class="nowrap">{{ formatTimestamp(row.lastActivityAt) }}</span>
        </template>
        <template #cell-createdAt="{ row }">
          <span class="nowrap">{{ formatTimestamp(row.createdAt) }}</span>
        </template>
        <template #cell-userFound="{ row }">
          <status-badge v-if="row.userFound" variant="success" label="Found" />
          <status-badge v-else variant="neutral" label="No match" />
        </template>
        <template #empty>No threads found</template>
      </data-table>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseEmailRepliesService } from '../services/firebaseEmailRepliesService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const columns = [
  { key: 'status', label: 'Status', width: '110px' },
  { key: 'customer', label: 'Customer', width: '230px' },
  { key: 'subject', label: 'Subject' },
  { key: 'messages', label: 'Messages', width: '160px' },
  { key: 'lastActivityAt', label: 'Last Activity', width: '150px' },
  { key: 'createdAt', label: 'Started', width: '150px' },
  { key: 'userFound', label: 'User', width: '110px' }
]

export default {
  name: 'EmailRepliesDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, StatusBadge
  },
  setup() {
    const router = useRouter()
    const service = new FirebaseEmailRepliesService()
    const threads = ref([])
    const loading = ref(true)
    const searchQuery = ref('')
    const activeFilter = ref('pending')

    const load = async () => {
      try {
        threads.value = await service.listThreads()
      } catch (e) {
        console.error('Error loading threads:', e)
      } finally {
        loading.value = false
      }
    }

    const threadStatus = (t) => {
      if ((t.pendingCount || 0) > 0) return 'pending'
      if ((t.messageCount || 0) > 0) return 'replied'
      return 'unknown'
    }

    const formatThreadStatus = (t) => {
      const s = threadStatus(t)
      if (s === 'pending') return 'Pending'
      if (s === 'replied') return 'Replied'
      return 'Empty'
    }

    const statusVariant = (t) => {
      const s = threadStatus(t)
      if (s === 'pending') return 'warning'
      if (s === 'replied') return 'success'
      return 'neutral'
    }

    const counts = computed(() => {
      const c = { pending: 0, replied: 0 }
      for (const t of threads.value) {
        const s = threadStatus(t)
        if (s === 'pending') c.pending++
        else if (s === 'replied') c.replied++
      }
      return c
    })

    const filterOptions = computed(() => [
      { value: 'pending', label: 'Pending', count: counts.value.pending },
      { value: 'replied', label: 'Replied', count: counts.value.replied },
      { value: 'all', label: 'All' }
    ])

    const filteredThreads = computed(() => {
      const q = searchQuery.value.trim().toLowerCase()
      return threads.value.filter(t => {
        const s = threadStatus(t)
        const statusOk = activeFilter.value === 'all' || s === activeFilter.value
        if (!statusOk) return false
        if (!q) return true
        return (
          (t.subject || '').toLowerCase().includes(q) ||
          (t.customerEmail || '').toLowerCase().includes(q) ||
          (t.customerName || '').toLowerCase().includes(q)
        )
      })
    })

    const formatTimestamp = (ts) => {
      if (!ts) return '-'
      const ms = ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime()
      if (isNaN(ms)) return '-'
      return new Date(ms).toLocaleString('tr-TR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      })
    }

    const truncate = (text, n) => {
      if (!text) return '-'
      return text.length > n ? text.substring(0, n) + '…' : text
    }

    const openThread = (row, event) => {
      if (event.metaKey || event.ctrlKey) {
        window.open(`/email-reply/${row.id}`, '_blank')
      } else {
        router.push(`/email-reply/${row.id}`)
      }
    }

    onMounted(load)

    return {
      threads,
      filteredThreads,
      loading,
      searchQuery,
      activeFilter,
      filterOptions,
      counts,
      columns,
      threadStatus,
      formatThreadStatus,
      statusVariant,
      formatTimestamp,
      truncate,
      openThread
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.nowrap { white-space: nowrap; }
.cust-name { color: var(--text-primary); font-weight: 500; }
.cust-email { color: var(--text-secondary); font-size: 11px; font-family: monospace; }
.subject {
  display: inline-block;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.msg-cell { display: inline-flex; align-items: center; gap: 6px; }
</style>
